import type {CurriedSelector, Reducer, Selector} from '@/redux/types';
import type {State as AppState} from '@/redux/typesNew';
import type {CrossBordersData, Price} from '@/redux/v1/models';
import type {
  FetchOfferListError,
  FetchOfferListPending,
  FetchOfferListSuccess,
} from '@/redux/v1/offers/list/actionTypes';
import type {
  BestOffer,
  Meta,
  Offer,
  State,
  StateByHotelId,
} from '@/redux/v1/offers/list/models';
import type {ActionHandlers} from '@travel-frontend/state-handling-sdk';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PENDING,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {curry, keyBy, sortBy} from 'lodash';
import {createSelector} from 'reselect';

import {FETCH_OFFERS} from '@/redux/v1/offers/list/actionTypes';
import {listState} from '@/redux/v1/offers/offersState';

const initialState: State = {
  loading: {},
  byHotelId: {},
  errors: {},
  requests: {},
};

const EMPTY_ARRAY: Array<never> = [];
const EMPTY_OBJECT = {};

// @ts-expect-error ...
const ignoreStaleResponses = (actionHandler) => (state, action) =>
  action.meta.requestId === state.requests[action.meta.hotelId]?.id
    ? actionHandler(state, action)
    : state;

const actionCreators: ActionHandlers<State> = {
  [PENDING(FETCH_OFFERS)]: (state: State, {meta}: FetchOfferListPending) => ({
    ...state,
    loading: {
      ...state.loading,
      [meta.hotelId]: true,
    },
    requests: {
      ...state.requests,
      [meta.hotelId]: {
        id: meta.requestId,
      },
    },
  }),
  [SUCCESS(FETCH_OFFERS)]: ignoreStaleResponses(
    (state: State, {data, meta}: FetchOfferListSuccess) => ({
      ...state,
      loading: {
        ...state.loading,
        [meta.hotelId]: false,
      },
      byHotelId: {
        ...state.byHotelId,
        [meta.hotelId]: {
          byId: keyBy(data.offers, (offer: Offer) => offer.id),
          ids: data.offers.map((offer: Offer) => offer.id),
          count: data.count,
          metaUnfiltered: data.metaUnfiltered,
          metaFiltered: data.metaFiltered,
          translations: data.translations,
          bestOffers: data.bestOffers,
          offerCode: data.offerCode,
        },
      },
      crossBorders: data.crossBorders,
      errors: {
        ...state.errors,
        [meta.hotelId]: null,
      },
    })
  ),
  [ERROR(FETCH_OFFERS)]: ignoreStaleResponses(
    (state: State, {error, meta}: FetchOfferListError) => {
      return {
        ...state,
        loading: {
          ...state.loading,
          [meta.hotelId]: false,
        },
        errors: {
          ...state.errors,
          [meta.hotelId]: error,
        },
      };
    }
  ),
};

export const listReducer: Reducer<State> =
  createReducerUsingActionHandlers<State>(initialState, actionCreators);

// @ts-expect-error ...
const getOfferStateByHotelId = createSelector(
  // @ts-expect-error ...
  [listState, (_, hotelId) => hotelId],
  (state: State, hotelId: string) => state.byHotelId[hotelId] || EMPTY_OBJECT
);

export const selectOffers = createSelector(
  [getOfferStateByHotelId, (_, hotelId) => hotelId],
  (offerState): Array<Offer> => Object.values(offerState.byId || EMPTY_OBJECT)
);

export const getOfferCountByHotelId = (): Selector<number> =>
  // @ts-expect-error ...
  createSelector(
    [selectOffers, (_, hotelId: string) => hotelId],
    (offers) => offers.length
  );

export const getLowestOfferPrice =
  (travellerCount: number) =>
  (): Selector<{
    totalPrice: null | Price;
    totalPriceInternal: null | Price;
    pricePerPerson: null | Price;
  }> =>
    // @ts-expect-error ...
    createSelector(
      // @ts-expect-error ...
      [selectOffers, (_, hotelId: string) => hotelId],
      (offers: Array<Offer>) => {
        const cheapestOffer = sortBy(
          offers,
          (offer) => offer.totalPriceDisplay.amount
        )[0];

        return cheapestOffer == null
          ? {
              totalPrice: null,
              totalPriceOriginal: null,
              totalPriceInternal: null,
              pricePerPerson: null,
              totalDiscount: null,
              totalDiscountOriginal: null,
              discountType: 'NO_DISCOUNT',
              discountPercentage: 0,
            }
          : {
              totalPrice: cheapestOffer.totalPriceDisplay,
              totalPriceOriginal: cheapestOffer.totalPriceOriginalDisplay,
              totalPriceInternal: cheapestOffer.totalPrice,
              pricePerPerson: {
                amount: cheapestOffer.totalPriceDisplay.amount / travellerCount,
                currency: cheapestOffer.totalPriceDisplay.currency,
              },
              totalDiscount: cheapestOffer.totalDiscountDisplay,
              totalDiscountOriginal: cheapestOffer.totalDiscount,
              discountType: cheapestOffer.discountType,
              discountPercentage: cheapestOffer.discountPercentage,
            };
      }
    );

export const getOfferIndexByHotelIdAndOfferId = () =>
  createSelector(
    [
      getOfferStateByHotelId,
      (_, hotelId: string) => hotelId,
      (_, _hotelId: string, offerId: string) => offerId,
    ],
    (offerState, _hotelId, offerId) => offerState?.ids?.indexOf(offerId)
  );

const selectStateByHotelId: CurriedSelector<
  string,
  null | undefined | StateByHotelId
> = curry(
  (hotelId: string, state: AppState) => listState(state).byHotelId[hotelId]
);

export const selectAreOffersLoading: CurriedSelector<string, boolean> = curry(
  (hotelId: string, state: AppState) =>
    listState(state).loading[hotelId] === true
);

export const selectAreOffersLoaded: CurriedSelector<string, boolean> = curry(
  (hotelId: string, state: AppState) =>
    listState(state).byHotelId[hotelId] !== undefined
);

export const selectHaveOffersFailedLoading: CurriedSelector<string, boolean> =
  curry((hotelId: string, state: AppState) =>
    Boolean(listState(state).errors[hotelId])
  );

export const selectOffer: CurriedSelector<
  {
    hotelId: string;
    offerId: string;
  },
  null | undefined | Offer
> = curry(
  ({hotelId, offerId}: {hotelId: string; offerId: string}, state: AppState) =>
    selectStateByHotelId(hotelId, state)?.byId?.[offerId]
);

export const selectMetaUnfiltered: CurriedSelector<
  string,
  null | undefined | Meta
> = curry(
  (hotelId: string, state: AppState) =>
    selectStateByHotelId(hotelId, state)?.metaUnfiltered
);

export const selectMetaFiltered: CurriedSelector<
  string,
  null | undefined | Meta
> = curry(
  (hotelId: string, state: AppState) =>
    selectStateByHotelId(hotelId, state)?.metaFiltered
);

export const selectTranslations: CurriedSelector<string, any> = curry(
  (hotelId: string, state: AppState) =>
    selectStateByHotelId(hotelId, state)?.translations || EMPTY_OBJECT
);

export const selectBestOffers: CurriedSelector<
  string,
  Array<BestOffer>
> = curry(
  (hotelId: string, state: AppState) =>
    selectStateByHotelId(hotelId, state)?.bestOffers ?? EMPTY_ARRAY
);

export const selectBestOffer: CurriedSelector<
  {
    hotelId: string;
    offerId: string;
  },
  null | undefined | BestOffer
> = curry(
  ({hotelId, offerId}: {hotelId: string; offerId: string}, state: AppState) => {
    const bestOffers = selectBestOffers(hotelId, state);
    for (const bestOffer of bestOffers) {
      if (bestOffer.offerID === offerId) {
        return bestOffer;
      }
    }
    return undefined;
  }
);

export const selectOfferCountFiltered: CurriedSelector<
  string,
  null | undefined | number
> = curry(
  (hotelId: string, state: AppState) =>
    selectStateByHotelId(hotelId, state)?.count
);

export const getCrossBordersData: Selector<CrossBordersData | undefined> = (
  state
) => listState(state).crossBorders;

export const selectOfferCode: CurriedSelector<string, string> = curry(
  (hotelId: string, state: AppState) =>
    selectStateByHotelId(hotelId, state)?.offerCode || ''
);
